/*
  LaplandSanta cart extras for Horizon (imported as @theme/ls-cart).
  - mutate(): one Ajax Cart API change, announced with Horizon's CartLinesUpdateEvent so the drawer,
    cart page and header count refresh through Horizon's own code.
  - enqueue(): cart changes run strictly one after another (bundle adds, protection, free gift).
  - <ls-shipping-protection>: opt-in toggle. <ls-cart-rules>: adds/removes the "Buy 3, get 1 free" gift.
  The free price comes from a real Shopify Buy X get Y discount; this file only manages the line.
*/
/* global Theme */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Formats cents like the store's money format, learned from a Liquid sample such as "£1,234,567.89". */
export function moneyFormatter(sample) {
  const t = document.createElement('textarea');
  t.innerHTML = sample || '';
  const m = t.value.match(/^([^\d]*)1(\D?)234(\D?)567(?:(\D)89)?(.*)$/s);
  if (!m) return (cents) => (cents / 100).toFixed(2);
  const [, prefix, sep1, sep2, decimal, suffix] = m;
  return (cents) => {
    const value = Math.round(Math.abs(cents));
    let out = String(decimal ? Math.floor(value / 100) : Math.round(value / 100)).replace(
      /\B(?=(\d{3})+(?!\d))/g,
      sep1 || sep2 || ''
    );
    if (decimal) out += decimal + String(value % 100).padStart(2, '0');
    return `${cents < 0 ? '-' : ''}${prefix}${out}${suffix}`;
  };
}

let queue = Promise.resolve();
/** @template T @param {() => Promise<T>} task @returns {Promise<T>} */
export function enqueue(task) {
  const run = queue.then(task, task);
  queue = run.catch(() => {});
  return run;
}

export async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/**
 * @param {HTMLElement} source - element that dispatches the cart events
 * @param {{ items?: Array<{id: number, quantity: number}>, change?: {id: string, quantity: number} }} op
 * @param {{ context?: string, detail?: object }} [options]
 * @returns {Promise<{ ok: boolean, message: string }>}
 */
export async function mutate(source, op, { context = 'cart', detail = {} } = {}) {
  const sections = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  const adding = !!op.items;
  const action = adding ? 'add' : op.change?.quantity === 0 ? 'remove' : 'update';
  const lines = adding
    ? op.items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }))
    : [{ ...op.change }];
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));

  try {
    const response = await fetch(adding ? Theme.routes.cart_add_url : Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...(adding ? { items: op.items } : op.change),
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    const failed = !response.ok || !!data.status || !!data.errors;
    const cart = adding || failed ? await fetchCart() : data;
    const message = failed ? String(data.description || data.message || data.errors || 'Cart update failed') : '';
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        ...detail,
        sections: failed ? undefined : data.sections ?? undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: source.localName,
        didError: failed,
      },
    });
    if (failed) source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    return { ok: !failed, message };
  } catch (error) {
    console.warn(`[${source.localName}]`, error);
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: String(error), code: 'SERVICE_UNAVAILABLE' }));
    return { ok: false, message: '' };
  }
}

/* ---------- Shipping Protection (opt-in toggle) ---------- */

class LsShippingProtection extends HTMLElement {
  static observedAttributes = ['data-in-cart', 'data-orphan', 'data-quantity'];
  #busy = false;

  connectedCallback() {
    this.addEventListener('change', this.#onToggle);
    queueMicrotask(() => this.#reconcile());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onToggle);
  }

  attributeChangedCallback() {
    if (!this.isConnected || this.#busy) return;
    const input = this.#input;
    if (input) input.checked = this.dataset.inCart === 'true';
    this.#reconcile();
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="checkbox"]'));
  }

  /** Never left alone in an otherwise empty cart, never more than once. */
  #reconcile() {
    if (this.dataset.orphan === 'true') this.#update(0);
    else if (Number(this.dataset.quantity) > 1) this.#update(1);
  }

  /** @param {Event} event */
  #onToggle = (event) => {
    const input = this.#input;
    if (!input || event.target !== input || this.#busy) return;
    if (input.checked !== (this.dataset.inCart === 'true')) this.#update(input.checked ? 1 : 0);
  };

  /** @param {0 | 1} quantity */
  #update(quantity) {
    if (this.#busy || !this.dataset.variantId) return;
    const inCart = this.dataset.inCart === 'true';
    this.#setBusy(true);
    enqueue(async () => {
      const { ok } = await mutate(
        this,
        quantity === 1 && !inCart
          ? { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] }
          : { change: { id: this.dataset.lineKey || String(this.dataset.variantId), quantity } }
      );
      this.#setBusy(false);
      if (!ok) {
        const input = this.#input;
        if (input) input.checked = inCart;
        this.#error(this.dataset.errorMessage || 'Something went wrong. Please try again.');
      }
    });
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
    const input = this.#input;
    if (input) input.disabled = busy;
    if (busy) this.#error('');
  }

  /** @param {string} message */
  #error(message) {
    const box = this.querySelector('[data-ls-error]');
    if (!(box instanceof HTMLElement)) return;
    box.textContent = message;
    box.hidden = !message;
  }
}

/* ---------- Free gift (Buy X, get 1 free) ---------- */

const BLOCK_KEY = 'ls-gift-blocked';
const session = {
  get: () => {
    try {
      return sessionStorage.getItem(BLOCK_KEY) === '1';
    } catch {
      return false;
    }
  },
  /** @param {boolean} on */
  set: (on) => {
    try {
      on ? sessionStorage.setItem(BLOCK_KEY, '1') : sessionStorage.removeItem(BLOCK_KEY);
    } catch {}
  },
};

class LsCartRules extends HTMLElement {
  static observedAttributes = ['data-paid', 'data-gift-qty', 'data-gift-charged'];
  #pending = false;

  connectedCallback() {
    queueMicrotask(() => this.#reconcile());
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#reconcile();
  }

  #reconcile() {
    if (this.#pending || this.dataset.giftEnabled !== 'true') return;
    const paid = Number(this.dataset.paid) || 0;
    const threshold = Number(this.dataset.threshold) || 3;
    const giftQty = Number(this.dataset.giftQty) || 0;
    const giftLine = this.dataset.giftKey || String(this.dataset.giftVariant);

    // Safety net: if Shopify did not make the gift free (discount missing or not applied), never charge for it.
    if (giftQty > 0 && this.dataset.giftCharged === 'true') {
      session.set(true);
      console.warn('[ls-cart-rules] The gift was not discounted to 0, so it was removed. Check the Buy X get Y discount.');
      return this.#run({ change: { id: giftLine, quantity: 0 } });
    }

    if (paid < threshold) session.set(false);
    const wanted = paid >= threshold && !session.get() ? 1 : 0;
    if (giftQty === wanted) return;
    if (giftQty === 0) return this.#run({ items: [{ id: Number(this.dataset.giftVariant), quantity: 1 }] });
    return this.#run({ change: { id: giftLine, quantity: wanted } });
  }

  /** @param {Parameters<typeof mutate>[1]} op */
  #run(op) {
    this.#pending = true;
    enqueue(async () => {
      const { ok } = await mutate(this, op);
      // A failed request (e.g. the gift product is unavailable) is not retried in a loop this session.
      if (!ok) session.set(true);
      this.#pending = false;
    });
  }
}

if (!customElements.get('ls-shipping-protection')) customElements.define('ls-shipping-protection', LsShippingProtection);
if (!customElements.get('ls-cart-rules')) customElements.define('ls-cart-rules', LsCartRules);
